/**
 * Admin Logs API Functions
 * Handles all API communication for admin logs
 */

import apiClient from '@lift-admin/utils/apiClient';
import {
  AdminLogEntry,
  LegacyLogEntry,
  LogsFilterState,
  LogsStats,
  ExportOptions,
  LogSeverity,
  ActionCategory,
} from '../types';
import {
  transformLegacyLog,
  calculateStats,
  exportToCSV,
  exportToJSON,
  categorizeAction,
} from '../utils';

// When VITE_USER_SERVICE_URL is set (via .env.local for local backends),
// hit User-Service directly. Local mounts at /v1/... (no gateway /user prefix).
// .env.development / staging / prod leave it unset → relative /user/v1/... via VITE_BASEURL.
const USER_SERVICE_URL = (import.meta.env.VITE_USER_SERVICE_URL as string | undefined)?.replace(/\/+$/, '');

/** Resolve path: gateway uses /user/v1/..., local User-Service uses /v1/... */
function userServiceUrl(gatewayPath: string): string {
  if (!USER_SERVICE_URL) {
    return gatewayPath;
  }
  const localPath = gatewayPath.replace(/^\/user/, '');
  return `${USER_SERVICE_URL}${localPath}`;
}

interface GetLogsParams {
  pageSize?: number;
  beforeTimestamp?: string;  // Timestamp-based pagination
  startDate?: Date | null;
  endDate?: Date | null;
  severity?: LogSeverity[];
  categories?: ActionCategory[];
  userId?: number;
  searchText?: string;
  /** Bypass server read-cache (explicit Refresh). */
  refresh?: boolean;
}

interface GetLogsResult {
  logs: AdminLogEntry[];
  oldestTimestamp?: string;  // Timestamp of the oldest log for pagination
  hasMore: boolean;          // Whether more logs are available
  stats: LogsStats;
}

/**
 * Fetch admin logs from the backend with server-side filtering
 * Uses timestamp-based pagination for reliable "load more" functionality
 */
export async function getAdminLogs(params: GetLogsParams = {}): Promise<GetLogsResult> {
  const { 
    pageSize = 50, 
    beforeTimestamp,  // Timestamp-based pagination
    startDate, 
    endDate,
    severity,
    categories,
    userId,
    searchText,
    refresh,
  } = params;
  
  const queryParams = new URLSearchParams();
  queryParams.set('pageSize', pageSize.toString());
  
  // For pagination: fetch logs older than this timestamp (always ISO)
  if (beforeTimestamp) {
    const cursor = new Date(beforeTimestamp);
    if (!Number.isNaN(cursor.getTime())) {
      queryParams.set('beforeTimestamp', cursor.toISOString());
    }
  }
  
  if (startDate) {
    queryParams.set('startDate', startDate.toISOString());
  }
  
  if (endDate) {
    queryParams.set('endDate', endDate.toISOString());
  }
  
  // Pass filters to backend for server-side filtering
  if (severity && severity.length > 0) {
    queryParams.set('severity', severity.join(','));
  }
  
  if (categories && categories.length > 0) {
    queryParams.set('categories', categories.join(','));
  }
  
  if (userId) {
    queryParams.set('userId', userId.toString());
  }
  
  if (searchText && searchText.trim()) {
    queryParams.set('search', searchText.trim());
  }

  if (refresh) {
    queryParams.set('refresh', '1');
  }

  // Warm user-image cache in the background — never block first paint on /admin/all.
  const cachedImages = getCachedUserImages();
  if (!cachedImages) {
    void fetchUserImages();
  }
  
  const res = await apiClient.get(
    `${userServiceUrl('/user/v1/admin/user/log')}?${queryParams.toString()}`
  );
  
  // Backend now returns enhanced format directly
  const rawLogs = res.data.response.logDetails || [];
  const oldestTimestamp = res.data.response.oldestTimestamp || undefined;
  const hasMore = res.data.response.hasMore || false;
  
  let logs: AdminLogEntry[] = rawLogs.map((log: any) => {
    // New format from backend (has action field)
    if (log.action) {
      const entry = log as AdminLogEntry;
      // Recover a specific category when the backend stored a generic/missing one
      // (e.g. alert logs arriving as "other"). Only upgrades — never downgrades an
      // already-specific category.
      if (!entry.action.category || entry.action.category === 'other') {
        const derived = categorizeAction(
          entry.action.type || '',
          entry.request?.url || (entry.payload as any)?.url
        );
        if (derived !== 'other') {
          entry.action = { ...entry.action, category: derived };
        }
      }
      return entry;
    }
    // Legacy format - transform it
    return transformLegacyLog(log as LegacyLogEntry);
  });

  // Apply avatars only when the cache is already warm (instant).
  if (cachedImages && cachedImages.size > 0) {
    logs = logs.map(log => {
      if (log.user?.id && !log.user.image) {
        const image = cachedImages.get(log.user.id);
        if (image) {
          return {
            ...log,
            user: { ...log.user, image },
          };
        }
      }
      return log;
    });
  }
  
  return {
    logs,
    oldestTimestamp,  // Use this for "load more"
    hasMore,
    stats: calculateStats(logs),
  };
}

/**
 * Fetch logs with filter state applied
 * Now passes filters to backend for server-side filtering
 */
export async function getFilteredLogs(
  filters: LogsFilterState,
  pagination: { pageSize: number; beforeTimestamp?: string }
): Promise<GetLogsResult> {
  // Pass all filters to backend
  const result = await getAdminLogs({
    pageSize: pagination.pageSize,
    beforeTimestamp: pagination.beforeTimestamp,
    startDate: filters.dateRange.startDate,
    endDate: filters.dateRange.endDate,
    severity: filters.severity.length > 0 ? filters.severity : undefined,
    categories: filters.categories.length > 0 ? filters.categories : undefined,
    userId: filters.users.length === 1 ? filters.users[0] : undefined,
    searchText: filters.searchText || undefined,
  });
  
  // Apply showOnlyErrors filter client-side (simple filter)
  let logs = result.logs;
  if (filters.showOnlyErrors) {
    logs = logs.filter(log => log.severity === 'ERROR');
  }
  
  // If multiple users selected, filter client-side
  if (filters.users.length > 1) {
    logs = logs.filter(log => filters.users.includes(log.user.id));
  }
  
  const stats = calculateStats(logs);
  
  return {
    logs,
    oldestTimestamp: result.oldestTimestamp,
    hasMore: result.hasMore,
    stats,
  };
}

/**
 * Cache for user images to avoid repeated API calls
 * Includes timestamp for cache expiration (5 minutes)
 */
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let userImagesCache: Map<number, string | null> | null = null;
let userImagesCacheTimestamp: number = 0;
let userImagesFetchPromise: Promise<Map<number, string | null>> | null = null;

/** Synchronous cache read — returns null when cold/expired. */
function getCachedUserImages(): Map<number, string | null> | null {
  if (userImagesCache && (Date.now() - userImagesCacheTimestamp) < CACHE_TTL_MS) {
    return userImagesCache;
  }
  return null;
}

/**
 * Fetch all admin users with their images from the database
 * Uses deduplication to prevent multiple simultaneous requests
 */
async function fetchUserImages(): Promise<Map<number, string | null>> {
  const cached = getCachedUserImages();
  if (cached) {
    return cached;
  }
  
  // Deduplicate concurrent requests
  if (userImagesFetchPromise) {
    return userImagesFetchPromise;
  }
  
  userImagesFetchPromise = (async () => {
    try {
      const res = await apiClient.get(userServiceUrl('/user/v1/admin/all'));
      const users = res.data.response || [];
      
      userImagesCache = new Map();
      users.forEach((user: any) => {
        if (user.id) {
          userImagesCache!.set(user.id, user.image || null);
        }
      });
      userImagesCacheTimestamp = Date.now();
      
      return userImagesCache;
    } catch (error) {
      console.error('Failed to fetch user images:', error);
      return userImagesCache || new Map();
    } finally {
      userImagesFetchPromise = null;
    }
  })();
  
  return userImagesFetchPromise;
}

/**
 * Enrich logs with user images from database
 */
export async function enrichLogsWithUserImages(logs: AdminLogEntry[]): Promise<AdminLogEntry[]> {
  const userImages = await fetchUserImages();
  
  return logs.map(log => {
    if (log.user?.id && !log.user.image) {
      const image = userImages.get(log.user.id);
      if (image) {
        return {
          ...log,
          user: {
            ...log.user,
            image,
          },
        };
      }
    }
    return log;
  });
}

/**
 * Clear user images cache (call when users are updated)
 */
export function clearUserImagesCache(): void {
  userImagesCache = null;
  userImagesCacheTimestamp = 0;
  userImagesFetchPromise = null;
}

/**
 * Fetch all admin users for the filter dropdown (id, name, email, image).
 * Reuses the same /admin/all call + cache as avatar enrichment.
 */
export async function fetchAdminUsersForFilter(): Promise<
  Array<{ id: number; name: string; email: string; image?: string | null }>
> {
  try {
    // Ensure image cache is warm; we still need names/emails from the API.
    const res = await apiClient.get(userServiceUrl('/user/v1/admin/all'));
    const users = res.data.response || [];

    // Keep image cache in sync while we're here
    userImagesCache = new Map();
    users.forEach((user: any) => {
      if (user.id) {
        userImagesCache!.set(user.id, user.image || null);
      }
    });
    userImagesCacheTimestamp = Date.now();

    return users
      .filter((u: any) => u?.id && (u.name || u.email))
      .map((u: any) => ({
        id: u.id,
        name: u.name || u.email || `User ${u.id}`,
        email: u.email || '',
        image: u.image || null,
      }))
      .sort((a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name)
      );
  } catch (error) {
    console.error('Failed to fetch admin users for filter:', error);
    return [];
  }
}

/**
 * Get unique users from logs for filter dropdown
 * Extracts users from already-fetched logs to avoid duplicate API calls
 */
export function extractUsersFromLogs(logs: AdminLogEntry[]): Array<{ id: number; name: string; email: string; image?: string | null }> {
  const usersMap = new Map<number, { id: number; name: string; email: string; image?: string | null }>();
  
  logs.forEach(log => {
    if (log.user && !usersMap.has(log.user.id)) {
      usersMap.set(log.user.id, {
        id: log.user.id,
        name: log.user.name,
        email: log.user.email,
        image: log.user.image || null,
      });
    }
  });
  
  return Array.from(usersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * @deprecated Use extractUsersFromLogs instead to avoid duplicate API calls
 */
export async function getLogUsers(): Promise<Array<{ id: number; name: string; email: string; image?: string | null }>> {
  const { logs } = await getAdminLogs({ pageSize: 1000 });
  return extractUsersFromLogs(logs);
}

/**
 * Export logs based on options
 */
export async function exportLogs(options: ExportOptions): Promise<string> {
  // Fetch logs with filters applied server-side
  let { logs } = await getAdminLogs({
    pageSize: 500,
    startDate: options.dateRange?.startDate,
    endDate: options.dateRange?.endDate,
    severity: options.filters?.severity,
    categories: options.filters?.categories,
    userId: options.filters?.users?.length === 1 ? options.filters.users[0] : undefined,
    searchText: options.filters?.searchText,
  });
  
  // Apply any remaining client-side filters
  if (options.filters?.showOnlyErrors) {
    logs = logs.filter(log => log.severity === 'ERROR');
  }
  
  if (options.filters?.users && options.filters.users.length > 1) {
    logs = logs.filter(log => options.filters!.users!.includes(log.user.id));
  }
  
  switch (options.format) {
    case 'csv':
      return exportToCSV(logs, options.includePayload);
    case 'json':
      return exportToJSON(logs);
    default:
      return exportToJSON(logs);
  }
}

/**
 * Download exported logs as a file
 */
export function downloadExport(content: string, format: 'csv' | 'json', filename?: string): void {
  const mimeType = format === 'csv' ? 'text/csv' : 'application/json';
  const extension = format;
  const defaultFilename = `admin-logs-${new Date().toISOString().split('T')[0]}.${extension}`;
  
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || defaultFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
